import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface StudentTokens {
  balance: number;
  total_purchased: number;
  total_spent: number;
  expires_at?: string;
}

export const useStudentCredits = (userId?: string) => {
  const [credits, setCredits] = useState<StudentTokens>({ balance: 0, total_purchased: 0, total_spent: 0, expires_at: undefined });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchCredits = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('student_credits')
        .select('balance, total_purchased, total_spent, expires_at')
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        // If no record exists, create one
        if (fetchError.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('student_credits')
            .insert({ user_id: userId, balance: 0, total_purchased: 0, total_spent: 0 });
          
          if (insertError) {
            console.error('Error creating credits record:', insertError);
            // Don't throw error for new users, just set default values
          }
          setCredits({ balance: 0, total_purchased: 0, total_spent: 0, expires_at: undefined });
        } else {
          throw fetchError;
        }
      } else {
        setCredits(data);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error fetching credits:', err);
      setError('Failed to fetch tokens');
      toast({
        title: 'Error',
        description: 'Failed to fetch token balance',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const processPaymentSuccess = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('process-payment-success', {
        body: { sessionId }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'Payment Successful!',
          description: `${data.credits} tokens added to your account.`,
        });
        
        // Refresh tokens after successful payment
        await fetchCredits();
      }
    } catch (error: any) {
      console.error('Error processing payment:', error);
      toast({
        title: 'Payment Processing Error',
        description: 'Failed to process payment. Please contact support.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchCredits();
  }, [userId]);

  return {
    credits,
    loading,
    error,
    refetch: fetchCredits,
    processPaymentSuccess
  };
};